import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import {
  User, Mail, Lock, Eye, EyeOff, Save, AlertTriangle,
  CheckCircle, Shield, Loader, Check, Copy
} from 'lucide-react';
import { formatCustomerId } from '../utils/formatId';

// ─── SECTION CARD ─────────────────────────────────────────────
const Section = ({ icon: Icon, title, desc, children }) => (
  <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
    <div className="px-6 py-5 border-b border-gray-800 flex items-center gap-3">
      <div className="w-9 h-9 bg-gray-800 rounded-xl flex items-center justify-center">
        <Icon className="w-4 h-4 text-green-400" />
      </div>
      <div>
        <h2 className="font-bold text-white">{title}</h2>
        {desc && <p className="text-gray-500 text-xs mt-0.5">{desc}</p>}
      </div>
    </div>
    <div className="p-6">{children}</div>
  </div>
);

// ─── INPUT ────────────────────────────────────────────────────
const Input = ({ label, type = 'text', value, onChange, placeholder, hint, disabled, rightEl }) => (
  <div>
    <label className="block text-sm font-medium text-gray-300 mb-1.5">{label}</label>
    <div className="relative">
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500
          focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-colors
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          ${rightEl ? 'pr-12' : ''}`}
      />
      {rightEl && <div className="absolute right-4 top-1/2 -translate-y-1/2">{rightEl}</div>}
    </div>
    {hint && <p className="text-gray-600 text-xs mt-1.5">{hint}</p>}
  </div>
);

// ─── PASSWORD STRENGTH ────────────────────────────────────────
const PASSWORD_RULES = [
  { test: (p) => p.length >= 8,   label: 'At least 8 characters' },
  { test: (p) => /[A-Z]/.test(p), label: 'One uppercase letter' },
  { test: (p) => /[a-z]/.test(p), label: 'One lowercase letter' },
  { test: (p) => /\d/.test(p),    label: 'One number' },
];

// ─── MAIN PAGE ────────────────────────────────────────────────
const AccountPage = () => {
  const { user, fetchMe } = useAuth();

  // Profile form
  const [profile, setProfile] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
  });
  const [savingProfile, setSavingProfile] = useState(false);

  // Email form
  const [email, setEmail] = useState(user?.email || '');
  const [emailPassword, setEmailPassword] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  // Password form
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  const [savingPassword, setSavingPassword] = useState(false);

  // UUID copy
  const [copiedUuid, setCopiedUuid] = useState(false);
  const [copiedId, setCopiedId] = useState(false);

  const copyToClipboard = (text, setter) => {
    navigator.clipboard.writeText(text);
    setter(true);
    setTimeout(() => setter(false), 2000);
  };

  // ── Save profile ────────────────────────────────────────────
  const saveProfile = async (e) => {
    e.preventDefault();
    if (!profile.first_name.trim() || !profile.last_name.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    setSavingProfile(true);
    try {
      await api.patch('/auth/profile', profile);
      toast.success('Name updated!');
      if (fetchMe) await fetchMe();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  // ── Save email ──────────────────────────────────────────────
  const saveEmail = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes('@')) {
      toast.error('Enter a valid email address');
      return;
    }
    if (!emailPassword) {
      toast.error('Enter your current password to confirm');
      return;
    }
    setSavingEmail(true);
    try {
      await api.patch('/auth/email', { email, password: emailPassword });
      toast.success('Email updated! Please verify your new address.');
      setEmailPassword('');
      if (fetchMe) await fetchMe();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update email');
    } finally {
      setSavingEmail(false);
    }
  };

  // ── Save password ───────────────────────────────────────────
  const savePassword = async (e) => {
    e.preventDefault();
    const failing = PASSWORD_RULES.filter(r => !r.test(passwords.new));
    if (failing.length > 0) {
      toast.error('New password does not meet requirements');
      return;
    }
    if (passwords.new !== passwords.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    if (!passwords.current) {
      toast.error('Enter your current password');
      return;
    }
    setSavingPassword(true);
    try {
      await api.patch('/auth/password', {
        current_password: passwords.current,
        new_password: passwords.new,
      });
      toast.success('Password changed successfully!');
      setPasswords({ current: '', new: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  const toggle = (field) => setShowPasswords(p => ({ ...p, [field]: !p[field] }));

  return (
    <div className="min-h-screen bg-gray-950 pt-24 pb-16 px-4">
      <div className="max-w-2xl mx-auto space-y-6">

        {/* Header */}
        <div className="mb-2">
          <h1 className="text-3xl font-black text-white">Account Settings</h1>
          <p className="text-gray-400 mt-1">Manage your personal info and security</p>
        </div>

        {/* Account overview */}
        <div className="bg-gradient-to-r from-green-500/5 to-gray-900 border border-green-500/10 rounded-2xl p-5 flex items-center gap-4">
          <div className="w-14 h-14 bg-green-500/10 border-2 border-green-500/20 rounded-2xl flex items-center justify-center text-xl font-black text-green-400 flex-shrink-0">
            {user?.first_name?.[0]}{user?.last_name?.[0]}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-white text-lg">{user?.first_name} {user?.last_name}</div>
            <div className="text-gray-400 text-sm">{user?.email}</div>
            <div className="flex items-center gap-3 mt-1.5">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                user?.role === 'admin' ? 'bg-purple-500/10 text-purple-400' :
                user?.role === 'support_agent' ? 'bg-blue-500/10 text-blue-400' :
                'bg-gray-700 text-gray-400'
              }`}>{user?.role}</span>
              {user?.email_verified
                ? <span className="flex items-center gap-1 text-xs text-green-400"><CheckCircle className="w-3 h-3" />Verified</span>
                : <span className="flex items-center gap-1 text-xs text-yellow-400"><AlertTriangle className="w-3 h-3" />Unverified</span>
              }
            </div>
          </div>
        </div>

        {/* Account IDs */}
        <Section icon={Shield} title="Account Identity" desc="Your unique identifiers for support purposes">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Customer ID</label>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 font-mono text-green-400 font-bold text-sm">
                  {formatCustomerId(user?.customer_number)}
                </div>
                <button
                  onClick={() => copyToClipboard(formatCustomerId(user?.customer_number), setCopiedId)}
                  className="flex items-center gap-1.5 px-4 py-3 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-xl text-gray-400 hover:text-white text-sm transition-colors"
                >
                  {copiedId ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  {copiedId ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <p className="text-gray-600 text-xs mt-1.5">Share this with support when reporting an issue</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Full UUID</label>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 font-mono text-gray-400 text-xs truncate">
                  {user?.id}
                </div>
                <button
                  onClick={() => copyToClipboard(user?.id, setCopiedUuid)}
                  className="flex items-center gap-1.5 px-4 py-3 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-xl text-gray-400 hover:text-white text-sm transition-colors flex-shrink-0"
                >
                  {copiedUuid ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  {copiedUuid ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
          </div>
        </Section>

        {/* Profile */}
        <Section icon={User} title="Profile" desc="Update your display name">
          <form onSubmit={saveProfile} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="First name"
                value={profile.first_name}
                onChange={e => setProfile({ ...profile, first_name: e.target.value })}
                placeholder="Steve"
              />
              <Input
                label="Last name"
                value={profile.last_name}
                onChange={e => setProfile({ ...profile, last_name: e.target.value })}
                placeholder="Creeper"
              />
            </div>
            <button
              type="submit"
              disabled={savingProfile}
              className="flex items-center gap-2 bg-green-500 hover:bg-green-400 disabled:opacity-50 text-gray-950 font-bold px-5 py-2.5 rounded-xl text-sm transition-colors"
            >
              {savingProfile ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save name
            </button>
          </form>
        </Section>

        {/* Email */}
        <Section icon={Mail} title="Email Address" desc="Your login email and where we send notifications">
          <form onSubmit={saveEmail} className="space-y-4">
            <Input
              label="Email address"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@gmail.com"
            />
            <Input
              label="Confirm with your current password"
              type="password"
              value={emailPassword}
              onChange={e => setEmailPassword(e.target.value)}
              placeholder="••••••••"
              hint="We require your password to change your email for security"
            />
            <button
              type="submit"
              disabled={savingEmail || email === user?.email}
              className="flex items-center gap-2 bg-green-500 hover:bg-green-400 disabled:opacity-50 disabled:cursor-not-allowed text-gray-950 font-bold px-5 py-2.5 rounded-xl text-sm transition-colors"
            >
              {savingEmail ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Update email
            </button>
            {!user?.email_verified && (
              <div className="flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-3 text-yellow-400 text-sm">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                Your email is not verified. Check your inbox for a verification link.
              </div>
            )}
          </form>
        </Section>

        {/* Password */}
        <Section icon={Lock} title="Password" desc="Change your account password">
          <form onSubmit={savePassword} className="space-y-4">
            <Input
              label="Current password"
              type={showPasswords.current ? 'text' : 'password'}
              value={passwords.current}
              onChange={e => setPasswords({ ...passwords, current: e.target.value })}
              placeholder="••••••••"
              rightEl={
                <button type="button" onClick={() => toggle('current')} className="text-gray-400 hover:text-white transition-colors">
                  {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              }
            />

            <Input
              label="New password"
              type={showPasswords.new ? 'text' : 'password'}
              value={passwords.new}
              onChange={e => setPasswords({ ...passwords, new: e.target.value })}
              placeholder="••••••••"
              rightEl={
                <button type="button" onClick={() => toggle('new')} className="text-gray-400 hover:text-white transition-colors">
                  {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              }
            />

            {/* Password strength */}
            {passwords.new && (
              <div className="grid grid-cols-2 gap-1.5">
                {PASSWORD_RULES.map(({ test, label }) => (
                  <div key={label} className={`flex items-center gap-1.5 text-xs ${test(passwords.new) ? 'text-green-400' : 'text-gray-600'}`}>
                    <Check className="w-3 h-3 flex-shrink-0" /> {label}
                  </div>
                ))}
              </div>
            )}

            <Input
              label="Confirm new password"
              type={showPasswords.confirm ? 'text' : 'password'}
              value={passwords.confirm}
              onChange={e => setPasswords({ ...passwords, confirm: e.target.value })}
              placeholder="••••••••"
              hint={passwords.confirm && passwords.new !== passwords.confirm ? "Passwords don't match" : ''}
              rightEl={
                <button type="button" onClick={() => toggle('confirm')} className="text-gray-400 hover:text-white transition-colors">
                  {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              }
            />

            <button
              type="submit"
              disabled={savingPassword || !passwords.current || !passwords.new || !passwords.confirm}
              className="flex items-center gap-2 bg-green-500 hover:bg-green-400 disabled:opacity-50 disabled:cursor-not-allowed text-gray-950 font-bold px-5 py-2.5 rounded-xl text-sm transition-colors"
            >
              {savingPassword ? <Loader className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              Change password
            </button>
          </form>
        </Section>

        {/* Danger zone */}
        <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-6">
          <h3 className="font-bold text-red-400 mb-1 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Danger Zone
          </h3>
          <p className="text-gray-400 text-sm mb-4">
            Need to delete your account? Contact our support team and we'll process it within 24 hours. All your servers will be stopped and your data permanently removed.
          </p>
          <a
            href="/tickets"
            className="inline-flex items-center gap-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400 px-4 py-2.5 rounded-xl text-sm transition-colors"
          >
            Contact support to delete account
          </a>
        </div>

      </div>
    </div>
  );
};

export default AccountPage;
